import { CONSULTATION_STATUS, CONSULTATION_TYPE, IndicationFileStatus } from '@abiglobalhealth/go-types'
import { Fragment, useState } from 'react'

import { ScrollToBottomButton } from '@/components/ScrollToBottomButton'
import { useConsultation } from '@/hooks/useConsultation'
import { useQuestion } from '@/hooks/useQuestion'
import { useScrollToBottom } from '@/hooks/useScrollToBottom'

import { DoctorAnswerCard } from './DoctorAnswerCard'
import { FollowUpComposer } from './FollowUpComposer'
import { PatientQuestionCard } from './PatientQuestionCard'
import { Report } from './Report'
import { ReportError } from './ReportError'
import { ReportLoading } from './ReportLoading'
import { ReportPrompt } from './ReportPrompt'
import { ResponseActions } from './ResponseActions'
import { useGenerateReport } from './useGenerateReport'

export const Answer = () => {
  const { consultation, canDoFollowUp } = useConsultation()
  const [showFollowUp, setShowFollowUp] = useState(false)
  const {
    containerRef: answerContainerRef,
    showScrollButton,
    scrollToBottom
  } = useScrollToBottom(`${consultation?.questions?.length}-${showFollowUp}`)
  const generateReport = useGenerateReport()
  const { resetQuestion } = useQuestion()

  const openFollowUp = () => {
    resetQuestion()
    setShowFollowUp(true)
  }

  if (!consultation) return null

  if (consultation.caseConsultationPdf?.status === IndicationFileStatus.ERROR) {
    return <ReportError />
  }

  if (consultation.caseConsultationPdf?.status === IndicationFileStatus.CREATED) {
    return <Report report={consultation.caseConsultationPdf} />
  }

  if (
    consultation.caseConsultationPdf?.status === IndicationFileStatus.CREATING &&
    consultation.status === CONSULTATION_STATUS.CLOSED_WITH_REPORT_ACCESS
  ) {
    return <ReportLoading />
  }

  if (consultation.consultationType !== CONSULTATION_TYPE.MEDICAL) {
    return <ReportPrompt />
  }

  return (
    <div className="relative flex flex-1 flex-col overflow-y-hidden">
      <div ref={answerContainerRef} className="flex flex-1 flex-col items-center gap-4 overflow-y-auto p-4" data-testid="answer-container">
        {consultation.questions?.map(({ answer, createdAt, professional, question }, index) => {
          if (!answer) return null

          return (
            <Fragment key={`${createdAt ?? 'question'}-${index}`}>
              {question && <PatientQuestionCard question={question} />}
              {professional && <DoctorAnswerCard answer={answer} professional={professional} />}
            </Fragment>
          )
        })}

        {showFollowUp ? (
          <FollowUpComposer onCancel={() => setShowFollowUp(false)} />
        ) : (
          <ResponseActions canFollowUp={canDoFollowUp} onFollowUp={openFollowUp} onFinish={generateReport} />
        )}
      </div>

      {showScrollButton && <ScrollToBottomButton onClick={scrollToBottom} />}
    </div>
  )
}
